import { useState, useEffect, useCallback } from 'react';
import { graduationAPI, graduierungAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Award, Check, Ban, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GraduierungUebersicht from '@/components/GraduierungUebersicht';
import AltbestandPanel from '@/components/AltbestandPanel';

const STATUS = {
  'offen': { label: 'Offen', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  'graduiert': { label: 'Graduiert', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'ignoriert': { label: 'Ignoriert', cls: 'bg-muted text-muted-foreground' },
};

function dateDE(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

export default function GraduierungPage() {
  const [status, setStatus] = useState('offen');
  /* Gürtel-Vorschläge (28.09.2026): eigener Reiter, der sich zuerst öffnet,
     solange Vorschläge offen sind — vorher steckten sie im Reiter Übersicht. */
  const [vorschlaegeOffen, setVorschlaegeOffen] = useState(0);
  const zaehleVorschlaege = useCallback(async () => {
    try { const r = await graduierungAPI.vorschlaege(); return (r.data.vorschlaege || []).length; } catch { return 0; }
  }, []);
  useEffect(() => {
    zaehleVorschlaege().then(n => { setVorschlaegeOffen(n); if (n > 0) setStatus(s => (s === 'offen' ? 'vorschlaege' : s)); });
  }, [zaehleVorschlaege]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchFlags = useCallback(async () => {
    if (status === 'uebersicht' || status === 'vorschlaege') { setLoading(false); return; }
    setLoading(true);
    setLoadError(false);
    try {
      const res = await graduationAPI.list(status);
      setFlags(res.data.flags || []);
    } catch (err) {
      setFlags([]);
      setLoadError(true);
      toast.error(err?.response?.data?.detail || 'Liste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const handleResolve = async (flag, newStatus) => {
    if (newStatus === 'graduiert' &&
        !window.confirm(`${flag.member_name} wirklich zum ${flag.target_belt_label} graduieren?\n\nDer Gürtel wird gesetzt und das Mitglied bekommt eine Benachrichtigung.`)) {
      return;
    }
    setBusyId(flag.id);
    try {
      await graduationAPI.resolve(flag.id, newStatus);
      toast.success(
        newStatus === 'graduiert'
          ? `${flag.member_name} zum ${flag.target_belt_label} graduiert`
          : `Meldung für ${flag.member_name} ignoriert`
      );
      fetchFlags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="graduierung-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Award className="w-6 h-6 text-primary" /> Graduierungsreife Mitglieder
        </h1>
        <p className="text-muted-foreground text-sm">
          Reife nach Trainings, Haltezeit und Streifen (Regeln unter Einstellungen) · Die Graduierung entscheidest du
        </p>
      </div>

      {/* Statusfilter */}
      <Tabs value={status} onValueChange={setStatus} className="w-full">
        <TabsList>
          <TabsTrigger value="vorschlaege" data-testid="tab-vorschlaege">
            Vorschläge{vorschlaegeOffen > 0 && <span className="ml-1.5 text-[10px] bg-primary text-primary-foreground rounded-full px-1.5">{vorschlaegeOffen}</span>}
          </TabsTrigger>
          <TabsTrigger value="offen" data-testid="tab-offen">
            Offen{status === 'offen' && flags.length > 0 && (
              <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5">{flags.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="graduiert" data-testid="tab-graduiert">Graduiert</TabsTrigger>
          <TabsTrigger value="ignoriert" data-testid="tab-ignoriert">Ignoriert</TabsTrigger>
          <TabsTrigger value="uebersicht" data-testid="tab-uebersicht">Übersicht</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Übersicht aller Mitglieder (Regelwerk) */}
      {status === 'vorschlaege' ? <AltbestandPanel onGeaendert={() => zaehleVorschlaege().then(setVorschlaegeOffen)} /> : status === 'uebersicht' ? <GraduierungUebersicht /> : loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={`skeleton-${i}`} className="h-16 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : loadError ? (
        <Card className="border-border/60">
          <CardContent className="p-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Die Liste konnte nicht geladen werden.</p>
            <Button size="sm" variant="outline" onClick={fetchFlags} data-testid="graduierung-retry">Erneut versuchen</Button>
          </CardContent>
        </Card>
      ) : flags.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-10" data-testid="graduierung-empty">
          {status === 'offen'
            ? 'Aktuell ist niemand graduierungsreif.'
            : `Keine Einträge mit Status „${STATUS[status]?.label || status}“.`}
        </p>
      ) : (
        <div className="space-y-2">
          {flags.map((f) => {
            const st = STATUS[f.status] || { label: f.status, cls: 'bg-muted' };
            return (
              <Card key={f.id} className="border-border/60">
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Award className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{f.member_name || 'Unbekannt'}</p>
                      {/* Gürtel-Übergang gut lesbar: „Weiß → Gelb“ */}
                      <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                        <span>{f.current_belt_label || f.current_belt}</span>
                        <ArrowRight className="w-3 h-3 flex-shrink-0" />
                        <span className="font-semibold text-foreground">{f.target_belt_label || f.target_belt}</span>
                        <span>· {f.trainings_at_flag} Trainings</span>
                        {f.flagged_at && <span>· {dateDE(f.flagged_at)}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    {f.status === 'offen' && (
                      <>
                        <Button
                          size="sm" className="h-8"
                          disabled={busyId === f.id}
                          onClick={() => handleResolve(f, 'graduiert')}
                          title="Graduieren"
                          data-testid={`graduate-${f.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 text-muted-foreground"
                          disabled={busyId === f.id}
                          onClick={() => handleResolve(f, 'ignoriert')}
                          title="Ignorieren"
                          data-testid={`ignore-${f.id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
